import { useRef, useState } from 'react'
import onboardingService from '../../../services/onboardingService'

const PAGE_SIZE = 10
// Ask the backend to collect at least this many prospects in one request so the
// whole list feeds the campaign — not just the pages the user clicked through.
const TARGET_RESULTS = 500

const EMPTY_RESULT = { items: null, loading: false, page: 1, hasMore: false, total: 0, icp: '', query: '', companies: [] }

const createInitialIcpResult = (savedIcpResult) =>
    savedIcpResult ? { ...savedIcpResult, loading: false } : EMPTY_RESULT

const sliceForPage = (all, page) => all.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

const useIcpProspects = (savedIcpResult, savedAllProspects) => {
    const [icpResult, setIcpResult] = useState(() => createInitialIcpResult(savedIcpResult))
    const [allProspects, setAllProspects] = useState(() =>
        Array.isArray(savedAllProspects) ? savedAllProspects : (savedIcpResult?.items || []),
    )
    const cacheRef = useRef(
        Array.isArray(savedAllProspects) && savedAllProspects.length
            ? {
                  icp: savedIcpResult?.icp || '',
                  fetched: true,
                  all: savedAllProspects,
                  query: savedIcpResult?.query || '',
                  companies: savedIcpResult?.companies || [],
              }
            : { icp: '', fetched: false, all: [], query: '', companies: [] },
    )

    const showPage = (icpText, page) => {
        const cache = cacheRef.current
        const items = sliceForPage(cache.all, page)
        setIcpResult({
            items: items.length ? items : (page === 1 ? null : []),
            loading: false,
            page,
            hasMore: page * PAGE_SIZE < cache.all.length,
            total: cache.all.length,
            icp: icpText,
            query: cache.query,
            companies: cache.companies,
        })
    }

    const searchProspects = async (icpText, page = 1) => {
        // Same ICP already collected — just page through the cached set client-side.
        if (cacheRef.current.icp === icpText && cacheRef.current.fetched) {
            showPage(icpText, page)
            return
        }

        cacheRef.current = { icp: icpText, fetched: false, all: [], query: '', companies: [] }
        setAllProspects([])
        setIcpResult(prev => ({ ...prev, loading: true, page: 1, icp: icpText }))

        try {
            const res = await onboardingService.searchIcpLeads({ icp: icpText, target: TARGET_RESULTS })
            const all = res?.data?.prospects || []
            cacheRef.current = {
                icp: icpText,
                fetched: true,
                all,
                query: res?.data?.query || '',
                companies: res?.data?.companies || [],
            }
            setAllProspects(all)
            showPage(icpText, 1)
        } catch {
            setIcpResult(prev => ({ ...prev, items: null, loading: false, hasMore: false, page: 1, icp: icpText }))
        }
    }

    const changePage = (nextPage) => {
        const cache = cacheRef.current
        if (icpResult.loading || nextPage < 1 || !cache.fetched) return
        if (nextPage > 1 && (nextPage - 1) * PAGE_SIZE >= cache.all.length) return
        showPage(cache.icp, nextPage)
    }

    return { icpResult, allProspects, searchProspects, changePage }
}

export default useIcpProspects
